import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, X, Trophy, Hourglass, FileCheck2, Lock } from "lucide-react";
import Fireworks from "./Fireworks";
import Phase2Tracker from "./Phase2Tracker";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STORAGE_KEY = "bdu_milestone_fom_preapproval_dismissed_v2";

// Opens the members-only FOM Pre-Approval Letter page in a new tab (hidden until an admin posts it).
function LetterButton() {
  const { user } = useAuth();
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    api.get("/milestone/letter").then((r) => setAvailable(!!r.data.available)).catch(() => {});
  }, []);
  if (!available) return null;
  const signedIn = user && user !== false;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <a href="/milestone-letter" target="_blank" rel="noopener noreferrer" data-testid="milestone-cta-letter"
         className="btn-ghost px-7 py-3.5 inline-flex items-center gap-2 border-bdu-gold/60">
        <FileCheck2 size={16} /> View FOM Pre-Approval Letter
      </a>
      {!signedIn && (
        <span className="text-[10px] uppercase tracking-[0.22em] text-bdu-cream/50 inline-flex items-center gap-1" data-testid="milestone-letter-members-note">
          <Lock size={10} /> Members only · sign in to view
        </span>
      )}
    </div>
  );
}

export default function MilestoneBanner() {
  const [open, setOpen] = useState(true);
  const [fireworksOn, setFireworksOn] = useState(false);

  // honor prior dismissal, but keep a small ribbon so visitors can re-open
  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(STORAGE_KEY);
      if (dismissed === "1") setOpen(false);
    } catch (e) { /* ignore */ }
  }, []);

  // start fireworks only when banner is actually visible
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setFireworksOn(true), 150);
    return () => clearTimeout(t);
  }, [open]);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
    setFireworksOn(false);
    setOpen(false);
  };

  const reopen = () => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    setOpen(true);
  };

  if (!open) {
    return (
      <button
        onClick={reopen}
        data-testid="milestone-reopen-ribbon"
        className="w-full flex items-center justify-center gap-3 py-2.5 text-xs uppercase tracking-[0.24em] font-semibold text-black transition-all hover:brightness-110"
        style={{
          background: "linear-gradient(90deg, #B8860B 0%, #D4AF37 35%, #F7D878 50%, #D4AF37 65%, #B8860B 100%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 6s linear infinite",
        }}
      >
        <Trophy size={14} />
        <span>Phase I Complete · Phase II In Progress · View Milestone Letter</span>
        <ArrowRight size={14} />
      </button>
    );
  }

  return (
    <section
      className="relative overflow-hidden border-b border-bdu-gold/30"
      data-testid="milestone-banner"
      style={{ backgroundColor: "#050505" }}
    >
      {/* golden aura backdrop */}
      <div
        className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 400px at 20% 20%, rgba(212,175,55,0.18) 0%, rgba(0,0,0,0) 60%)," +
            "radial-gradient(700px 300px at 80% 80%, rgba(227,38,54,0.10) 0%, rgba(0,0,0,0) 60%)," +
            "radial-gradient(600px 300px at 50% 110%, rgba(46,164,79,0.10) 0%, rgba(0,0,0,0) 60%)",
        }}
      />
      {/* subtle grain */}
      <div className="absolute inset-0 pointer-events-none grain" />

      {/* Fireworks layer */}
      <Fireworks active={fireworksOn} duration={9000} intensity="full" />

      {/* Dismiss button */}
      <button
        onClick={dismiss}
        aria-label="Close milestone announcement"
        data-testid="milestone-dismiss-btn"
        className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center rounded-full border border-bdu-gold/40 text-bdu-cream/70 hover:text-bdu-gold hover:border-bdu-gold transition-colors bg-black/40 backdrop-blur-sm"
      >
        <X size={16} />
      </button>

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        {/* HERO IMAGE — chrome TDSP FCU logo with mesmerizing spotlight scan */}
        <div
          className="relative mx-auto mb-10 sm:mb-14 max-w-xl aspect-square overflow-hidden rounded-sm border border-bdu-gold/30 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.4)] group"
          data-testid="milestone-hero-image"
        >
          {/* chrome gradient frame */}
          <div
            className="absolute -inset-[2px] rounded-sm pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, #F5F5F5 0%, #808080 25%, #FFFFFF 50%, #4A4A4A 75%, #E0E0E0 100%)",
              zIndex: 0,
            }}
          />
          {/* image */}
          <img
            src="/brand/historic-milestone-hero.png"
            alt="Proposed Trillion Dollar Spending Power Federal Credit Union — chrome logo"
            className="relative z-10 w-full h-full object-cover"
            data-testid="milestone-hero-image-img"
          />

          {/* PROPOSED compliance badge — NCUA requires "Proposed" preceding
              the credit union name until charter is granted */}
          <div
            className="absolute top-4 left-4 z-[60] pointer-events-none"
            data-testid="milestone-hero-proposed-badge"
          >
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 border backdrop-blur-md"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(20,15,0,0.72) 100%)",
                borderColor: "rgba(247,216,120,0.9)",
                boxShadow:
                  "0 8px 20px -6px rgba(212,175,55,0.55), inset 0 1px 0 rgba(247,216,120,0.3)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: "#F7D878",
                  boxShadow: "0 0 10px #F7D878, 0 0 20px rgba(247,216,120,0.6)",
                  animation: "proposed-pulse 2.2s ease-in-out infinite",
                }}
              />
              <span
                className="text-[0.6rem] sm:text-[0.65rem] font-bold uppercase tracking-[0.3em]"
                style={{
                  background:
                    "linear-gradient(135deg, #FFFFFF 0%, #F7D878 50%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Proposed · Pending NCUA Charter
              </span>
            </div>
          </div>

          {/* dim base layer — image breathes a hair below full brightness so the
              scan line reads as a true spotlight unveiling detail */}
          <div
            className="pointer-events-none absolute inset-0 z-20"
            style={{
              background: "rgba(0,0,0,0.32)",
              mixBlendMode: "multiply",
            }}
          />

          {/* SPOTLIGHT BAND — travels top↔bottom in a mesmerizing 7s loop.
              A soft, tall vertical glow with gold+white band that locally
              brightens whatever it crosses. */}
          <div
            className="pointer-events-none absolute inset-x-0 z-30"
            style={{
              top: 0,
              bottom: 0,
              animation: "spotlight-scan 7s cubic-bezier(0.65,0,0.35,1) infinite",
            }}
          >
            <div
              style={{
                height: "32%",
                width: "100%",
                background:
                  "radial-gradient(ellipse 70% 55% at 50% 50%, rgba(255,245,210,0.95) 0%, rgba(247,216,120,0.75) 18%, rgba(212,175,55,0.42) 38%, rgba(0,0,0,0) 70%)",
                filter: "blur(2px) saturate(1.2)",
                mixBlendMode: "screen",
              }}
            />
          </div>

          {/* thin bright scan line centered inside the band for an extra
              laser-ribbon shine */}
          <div
            className="pointer-events-none absolute inset-x-0 z-40"
            style={{
              top: 0,
              bottom: 0,
              animation: "spotlight-scan 7s cubic-bezier(0.65,0,0.35,1) infinite",
            }}
          >
            <div
              style={{
                height: "6%",
                marginTop: "13%",
                width: "100%",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,250,220,0.95) 50%, rgba(255,255,255,0) 100%)",
                filter: "blur(1.2px)",
                boxShadow: "0 0 36px 8px rgba(247,216,120,0.75)",
                mixBlendMode: "screen",
              }}
            />
          </div>

          {/* diagonal "surface shine" that drifts the opposite direction so
              the mirror in the logo feels alive */}
          <div
            className="pointer-events-none absolute inset-0 z-50 opacity-50"
            style={{
              background:
                "linear-gradient(100deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0) 60%)",
              backgroundSize: "250% 100%",
              animation: "spotlight-sheen 9s linear infinite",
              mixBlendMode: "screen",
            }}
          />

          {/* corner glints */}
          <div
            className="pointer-events-none absolute top-3 left-3 z-50 w-2 h-2 rounded-full"
            style={{ background: "rgba(255,245,210,0.9)", boxShadow: "0 0 12px rgba(247,216,120,0.9)" }}
          />
          <div
            className="pointer-events-none absolute bottom-3 right-3 z-50 w-2 h-2 rounded-full"
            style={{ background: "rgba(255,245,210,0.9)", boxShadow: "0 0 12px rgba(247,216,120,0.9)" }}
          />

          <style>{`
            @keyframes spotlight-scan {
              0%   { transform: translateY(-20%); }
              45%  { transform: translateY(108%); }
              55%  { transform: translateY(108%); }
              100% { transform: translateY(-20%); }
            }
            @keyframes spotlight-sheen {
              0%   { background-position: 150% 0; }
              100% { background-position: -150% 0; }
            }
            @keyframes proposed-pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.55; transform: scale(1.35); }
            }
          `}</style>
        </div>

        {/* Compliance caption — the full name rendered with "Proposed" so it
            reads correctly even when scanned without context */}
        <p
          className="text-center text-[0.65rem] sm:text-xs uppercase tracking-[0.28em] text-bdu-cream/70 mb-10 sm:mb-14 -mt-6 sm:-mt-10"
          data-testid="milestone-hero-proposed-caption"
        >
          <span className="text-bdu-gold font-semibold">Proposed</span>
          <span className="mx-2 text-bdu-cream/40">·</span>
          Trillion Dollar Spending Power Federal Credit Union
        </p>

        {/* Eyebrow + badges */}
        <div className="flex flex-wrap items-center gap-3 justify-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-black"
            style={{
              background: "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #B8860B 100%)",
              boxShadow: "0 8px 24px -8px rgba(212,175,55,0.6)",
            }}
            data-testid="milestone-eyebrow"
          >
            <Sparkles size={12} /> A Historic Milestone
          </span>
          <span className="hidden sm:inline-block w-10 h-px bg-bdu-gold/40" />
          <span className="text-[10px] text-bdu-cream/60 uppercase tracking-[0.3em]">
            FOM Pre-Approval · October 21, 2026
          </span>
        </div>

        {/* Headline */}
        <h2
          className="mt-7 text-center font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-bdu-cream tracking-tight animate-fade-up"
          data-testid="milestone-headline"
        >
          Phase I: <span className="text-bdu-gold italic">Complete.</span>
          <br className="hidden sm:block" />
          <span className="sm:inline"> Phase II: </span>
          <span className="text-bdu-gold italic">In Progress.</span>
        </h2>

        {/* Twin status badges */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <div
            className="flex items-center gap-3 px-5 py-3 border border-bdu-gold/40 bg-black/50 backdrop-blur-sm animate-fade-up"
            style={{ animationDelay: "120ms" }}
            data-testid="milestone-badge-phase1"
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #2EA44F, #1B6B32)" }}>
              <Check size={18} className="text-white" strokeWidth={3} />
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-cream/60">Phase I · Complete</div>
              <div className="font-serif text-lg text-bdu-cream">Field of Membership (FOM) Pre-Approval Letter</div>
              <div className="text-[11px] text-bdu-gold" data-testid="milestone-phase1-date">October 21, 2026</div>
            </div>
          </div>

          <div
            className="flex items-center gap-3 px-5 py-3 border border-bdu-gold/40 bg-black/50 backdrop-blur-sm animate-fade-up"
            style={{ animationDelay: "220ms" }}
            data-testid="milestone-badge-phase2"
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #D4AF37, #8B6914)" }}>
              <Hourglass size={16} className="text-black" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-cream/60">Phase II</div>
              <div className="font-serif text-lg text-bdu-cream">In Progress</div>
            </div>
          </div>
        </div>

        <Phase2Tracker />

        {/* The Letter — framed */}
        <div
          className="relative mt-12 mx-auto max-w-3xl animate-fade-up"
          style={{ animationDelay: "320ms" }}
          data-testid="milestone-letter"
        >
          {/* corner ornaments */}
          <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-bdu-gold" />
          <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-bdu-gold" />
          <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-bdu-gold" />
          <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-bdu-gold" />

          <div
            className="relative px-8 sm:px-12 py-10 sm:py-14 border border-bdu-gold/25 bg-black/60 backdrop-blur-sm"
            style={{
              boxShadow:
                "0 40px 80px -30px rgba(212,175,55,0.35), inset 0 1px 0 rgba(212,175,55,0.08)",
            }}
          >
            <div className="text-center text-[10px] uppercase tracking-[0.32em] text-bdu-gold/80">
              A Letter to the Movement
            </div>
            <div className="mx-auto mt-4 w-16 h-px bg-bdu-gold/40" />

            <p className="mt-8 font-serif text-xl sm:text-2xl leading-relaxed text-bdu-cream italic">
              Today, we rise.
            </p>

            <p className="mt-6 text-bdu-cream/85 leading-loose text-[15px] sm:text-base">
              For over a century, our communities watched our dollars leave at
              sunset and never return at dawn. Today — <span className="text-bdu-gold">that story
              bends</span>.
            </p>

            <p className="mt-6 text-bdu-cream/85 leading-loose text-[15px] sm:text-base">
              With a quiet, relentless hand, our organizing group has submitted
              <span className="text-bdu-cream"> every Phase 1 document </span>
              required by the National Credit Union Administration. Every signature.
              Every disclosure. Every page of our Field of Membership study for
              <span className="text-bdu-cream"> Fulton County, DeKalb County, and the people of College Park</span>.
              And on <span className="text-bdu-gold">October 21, 2026</span>, we received our
              <span className="text-bdu-cream"> Field of Membership (FOM) Pre-Approval Letter</span>.
            </p>

            <p className="mt-6 text-bdu-cream/85 leading-loose text-[15px] sm:text-base">
              And <span className="text-bdu-cream">Phase 2 is now in progress</span> — the
              Business Plan, Capital Projections, Policies &amp; Procedures, and
              Marketing Plan are being prepared for NCUA review.
            </p>

            <p className="mt-6 text-bdu-cream/85 leading-loose text-[15px] sm:text-base">
              This is not a dream. This is a
              <span className="text-bdu-gold"> proposed federal credit union </span>
              being built brick by brick, pledge by pledge, by the hands of the
              people it will serve.
            </p>

            <p className="mt-6 text-bdu-cream/85 leading-loose text-[15px] sm:text-base">
              We are not asking for permission to exist. We are documenting that
              <span className="italic"> we already do.</span>
            </p>

            <p className="mt-8 font-serif text-xl sm:text-2xl leading-relaxed text-bdu-cream">
              To every pledger. Every supporter. Every ancestor whose name we
              carry — <span className="text-bdu-gold">thank you</span>.
            </p>

            <p className="mt-4 text-bdu-cream/75 leading-loose text-[15px] sm:text-base">
              The vault is being built. The charter is coming.
            </p>

            {/* Signature */}
            <div className="mt-10 flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-cream/50">
                  With unshakable resolve,
                </div>
                <div className="mt-2 font-serif text-xl text-bdu-gold italic">
                  The Black Dollars United Organizing Group
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-bdu-cream/50">
                  Proposed Trillion Dollar Spending Power Federal Credit Union
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-cream/50">Milestone</div>
                <div className="mt-1 font-serif text-lg text-bdu-cream">Phase I → Phase II</div>
                <div className="text-[11px] text-bdu-cream/50">October 21, 2026</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div
          className="mt-10 flex flex-wrap justify-center items-start gap-4 animate-fade-up"
          style={{ animationDelay: "420ms" }}
        >
          <LetterButton />
          <Link
            to="/credit-union"
            data-testid="milestone-cta-pledge"
            className="btn-gold px-7 py-3.5"
          >
            Pledge Your Support <ArrowRight size={16} />
          </Link>
          <Link
            to="/credit-union#leaderboard"
            data-testid="milestone-cta-refer"
            className="btn-ghost px-7 py-3.5"
          >
            Rally Your Circle
          </Link>
        </div>

        {/* NCUA compliance footnote */}
        <p
          className="mt-8 text-center text-[11px] text-bdu-cream/45 italic leading-relaxed max-w-2xl mx-auto"
          data-testid="milestone-compliance-note"
        >
          Proposed Credit Union — pending NCUA charter approval. Pledges indicate
          non-binding intent to become a charter supporter upon NCUA approval.
          Funds are not accepted as deposits.
        </p>
      </div>
    </section>
  );
}
